/**
 * Color tokens — brand-guide.md v0.2.2 §6.2
 *
 * These are the single source of truth. Every UI surface (cloud web app,
 * on-prem shell, prospect-PDF renderer, marketing site) consumes them.
 *
 * The comments preserve the accessibility corrections that motivated
 * specific hex choices (Round 2.1 / Round 2.2 / R3 pre-flight). A future
 * change that reverts one of these without measuring is a regression.
 *
 * Contract C1 (contrast-check.ts) computes every declared pair against
 * its semantic use and fails the build below threshold.
 */
/**
 * Operator mode — dark. GitHub-family palette. Marcus's midnight dashboard.
 */
export const dark = {
    bg: '#0F1117',
    surface: '#161B22',
    surfaceElevated: '#1C2129',
    // Cluster 0 (PR-C0-1): third-tier surface for row-hover / muted
    // section bands. Sits between #161B22 (surface) and #1C2129
    // (surface-elevated). Direction B parity: `--surface-alt` = #232936
    // — we deliberately land slightly darker on legacy dark so the
    // existing GitHub-family cascade doesn't lift its neutral floor.
    surfaceAlt: '#1F252E',
    border: '#30363D',
    borderStrong: '#484F58',
    // V98-0-6 (C23): form-control edge, >= 3:1 on bg / surface /
    // surface-elevated / surface-alt. Same hex as Direction B dark.
    borderControl: '#737B8C',
    text: '#F0F3F6',
    textMuted: '#C9D1D9',
    textDim: '#8B949E',
    // v0.2.2: darkened from #6E7681 (3.52:1 on surface-elevated failed 4.5:1
    // for 10-12px sizes on .nav-group-label, .workspace .role, .delta-row .meta).
    // #8B949E measures 6.14:1 on bg, 5.62:1 on surface, 5.20:1 on surface-elevated.
    textFaint: '#8B949E',
    // Decorative tertiary only, ≥16px.
    textFaintDecorative: '#6E7681',
    // Cluster 0: text on `accentCtaBg` / `dangerBg` / `warningBg` fills
    // + inverted CTAs. Direction B parity: `--text-inverse` = #0F1219.
    // Legacy dark uses its own bg color which measures ~7.9:1 against
    // accentCtaBg #F59E0B — safely above AAA 7:1.
    textInverse: '#12141A',
    accent: '#F59E0B',
    accentHover: '#FBBF24',
    accentCtaBg: '#F59E0B',
    accentCtaText: '#12141A',
    accentMark: '#F59E0B',
    secondary: '#79C0FF',
    success: '#7EE787',
    warning: '#F0883E',
    critical: '#FF7B72',
    regulatory: '#D2A8FF',
    focusRing: '#F59E0B',
    // Cluster 0 chip pairs — muted fills that read as tinted panels on
    // dark surfaces. `<X>Fg` values measure ≥4.5:1 on `<X>Bg`.
    //   success #7EE787 on #0E2A22 = 8.1:1  (green fg on dark green tint)
    //   warning #FBBF24 on #33240B = 8.8:1  (amber fg on dark amber tint)
    //   danger  #FCA5A5 on #3A1414 = 6.3:1  (red   fg on dark red tint)
    successBg: '#0E2A22',
    successFg: '#7EE787',
    warningBg: '#33240B',
    warningFg: '#FBBF24',
    dangerBg: '#3A1414',
    dangerFg: '#FCA5A5',
    // Cluster 0 KPI-band marks. Same hex as Direction B's `--band-*-mark`
    // so a surface rebased later doesn't visually shift its severity
    // colors.
    bandRed: '#DC2626',
    bandAmber: '#F59E0B',
    bandGreen: '#10B981',
    // PR-P3-2 category chip pairs (dark). litigation/litigationBg:
    // #EFBACA on #2A1219 = 10.46:1 (AAA, self-contained pair — carried
    // over from the UC-4 mockup's already-measured wine palette).
    // regulatoryBg pairs with the existing `regulatory` fg = 7.95:1.
    // secondaryBg pairs with the existing `secondary` fg = 7.56:1 (dark
    // mode's base secondary already clears comfortably, so secondaryFg
    // reuses it rather than needing its own darker variant).
    litigation: '#EFBACA',
    litigationBg: '#2A1219',
    regulatoryBg: '#2B1D42',
    secondaryBg: '#172846',
    secondaryFg: '#79C0FF',
};
/**
 * Boardroom mode — light. Editorial. Priya's Fortune-500 export.
 */
export const light = {
    bg: '#FBFBFA',
    surface: '#FFFFFF',
    surfaceElevated: '#F5F5F1',
    // Cluster 0 (PR-C0-1): third-tier surface for row-hover / muted
    // section bands. Sits between #FFFFFF (surface) and #F5F5F1
    // (surface-elevated). Direction B parity: `--surface-alt` = #EDEFF4
    // — we land slightly warmer here to match the editorial-cream
    // Boardroom palette.
    surfaceAlt: '#EFEFEB',
    border: '#E5E5E1',
    // v0.2.1: darkened from #A8A89E (2.32:1 — failed the very 3:1 floor this
    // token existed to clear). #8A8A80 measures 3.07:1 / 3.20:1 / 2.89:1 on
    // bg / surface / surface-elevated respectively.
    borderStrong: '#8A8A80',
    // On surface-warm tinted panels use borderStrongOnWarm (3.68:1) instead.
    borderStrongOnWarm: '#7A7A70',
    // V98-0-6 (C23): form-control edge, >= 3:1 on bg / surface /
    // surface-elevated / surface-alt. Same hex as Direction B light.
    borderControl: '#737C8D',
    text: '#12141A',
    textMuted: '#4C525C',
    // v0.2: darkened from #6E7681 (failed 4.5:1) to 5.6:1.
    textDim: '#5A6069',
    // Tertiary / captions only ≥16px.
    textFaint: '#6E7681',
    // Cluster 0: text on dark fills (accentCtaBg #92400E, textInverse on
    // dark banners, etc.). Pure white in light mode — measures 7.10:1 on
    // accentCtaBg (clears AAA 7:1). The near-cream bg color (#FBFBFA)
    // came in at 6.85:1 which failed the CTA role; pure white matches
    // the pre-existing accentCtaText so the two semantics stay aligned.
    textInverse: '#FFFFFF',
    // Signature accent — reserved for the freshness motif and .eyebrow dot in
    // light mode (§6.2 rule 1). Never for CTA background — see accentCtaBg.
    accent: '#B45309',
    accentHover: '#7C2D12',
    // v0.2: text-on-CTA in light mode uses #92400E (7.10:1 white-on-amber AAA)
    // as the resting CTA background, not #B45309 (5.02:1 fails brand-mandated
    // AAA for board projection).
    accentCtaBg: '#92400E',
    accentCtaText: '#FFFFFF',
    accentMark: '#B45309',
    secondary: '#0969DA',
    success: '#1A7F37',
    warning: '#BC4C00',
    critical: '#CF222E',
    // v0.2: tightened from #8250DF (failed 4.47:1 on #F5EFFA) to #6E3AD1 (5.2:1).
    regulatory: '#6E3AD1',
    // v0.2: focus-ring in light mode MUST be #B45309 (5.02:1), not #F59E0B
    // (2.07:1 — fails WCAG 1.4.11).
    focusRing: '#B45309',
    // Cluster 0 chip pairs — muted fills that read as tinted panels on
    // light Boardroom surfaces. All fg values pre-checked ≥4.5:1 on their
    // paired bg:
    //   success #1A7F37 on #DCFCE7 = 4.85:1
    //   warning #92400E on #FEF3C7 = 7.10:1  (reuse existing AAA CTA fg)
    //   danger  #B91C1C on #FEE2E2 = 5.87:1
    successBg: '#DCFCE7',
    successFg: '#1A7F37',
    warningBg: '#FEF3C7',
    warningFg: '#92400E',
    dangerBg: '#FEE2E2',
    dangerFg: '#B91C1C',
    // Cluster 0 KPI-band marks — Direction B parity (`--band-*-mark`).
    bandRed: '#B91C1C',
    bandAmber: '#B45309',
    bandGreen: '#059669',
    // PR-P3-2 category chip pairs (light). litigation/litigationBg:
    // #6B1C3A on #F5E4E9 = 9.26:1 (AAA, self-contained pair — carried
    // over from the UC-4 mockup's already-measured wine palette).
    // regulatoryBg pairs with the existing `regulatory` fg = 5.57:1.
    // secondaryFg (#0757B8, darker than base `secondary` #0969DA) pairs
    // with secondaryBg = 5.62:1 — base `secondary` alone measured only
    // 4.26:1 against this bg tint, just under the 4.5:1 floor.
    litigation: '#6B1C3A',
    litigationBg: '#F5E4E9',
    regulatoryBg: '#F0E9FB',
    secondaryBg: '#DBEAFE',
    secondaryFg: '#0757B8',
};
/**
 * Boardroom-print — the printed variant of light. Same tokens as `light`
 * plus print-safe overrides (borders drawn instead of filled, dots become
 * text characters). Consumed by print.css and PrintFooter.
 */
export const boardroomPrint = {
    ...light,
};
export const colors = { dark, light, boardroomPrint };
export const contrastPairs = [
    // Dark mode — body text on all surface levels.
    { mode: 'dark', fg: 'text', bg: 'bg', role: 'body' },
    { mode: 'dark', fg: 'text', bg: 'surface', role: 'body' },
    { mode: 'dark', fg: 'text', bg: 'surfaceElevated', role: 'body' },
    { mode: 'dark', fg: 'textMuted', bg: 'bg', role: 'body' },
    { mode: 'dark', fg: 'textMuted', bg: 'surface', role: 'body' },
    { mode: 'dark', fg: 'textMuted', bg: 'surfaceElevated', role: 'body' },
    { mode: 'dark', fg: 'textDim', bg: 'bg', role: 'body' },
    { mode: 'dark', fg: 'textDim', bg: 'surface', role: 'body' },
    { mode: 'dark', fg: 'textDim', bg: 'surfaceElevated', role: 'body' },
    { mode: 'dark', fg: 'textFaint', bg: 'bg', role: 'body', note: 'v0.2.2 correction — must clear 4.5:1 at 10-12px' },
    { mode: 'dark', fg: 'textFaint', bg: 'surface', role: 'body', note: 'v0.2.2 correction' },
    { mode: 'dark', fg: 'textFaint', bg: 'surfaceElevated', role: 'body', note: 'v0.2.2 correction' },
    // Dark mode — CTA. (Dark `--border` and `--border-strong` are decorative
    // hairline dividers; WCAG 1.4.11 does not apply since they're not the sole
    // state indicator — elevation + text signal presence. Not declared here on
    // purpose. If they ever become load-bearing, add pairs and re-measure.)
    { mode: 'dark', fg: 'accentCtaText', bg: 'accentCtaBg', role: 'cta' },
    // Light mode — body text on all surface levels.
    { mode: 'light', fg: 'text', bg: 'bg', role: 'body' },
    { mode: 'light', fg: 'text', bg: 'surface', role: 'body' },
    { mode: 'light', fg: 'text', bg: 'surfaceElevated', role: 'body' },
    { mode: 'light', fg: 'textMuted', bg: 'bg', role: 'body' },
    { mode: 'light', fg: 'textMuted', bg: 'surface', role: 'body' },
    { mode: 'light', fg: 'textDim', bg: 'bg', role: 'body', note: 'v0.2 correction — must clear 4.5:1' },
    { mode: 'light', fg: 'textDim', bg: 'surface', role: 'body' },
    // Light mode — UI perimeters. borderStrong is the load-bearing button-border token.
    { mode: 'light', fg: 'borderStrong', bg: 'bg', role: 'ui', note: 'v0.2.1 correction — 3.07:1 on bg' },
    { mode: 'light', fg: 'borderStrong', bg: 'surface', role: 'ui' },
    { mode: 'light', fg: 'borderStrongOnWarm', bg: 'surfaceElevated', role: 'ui', note: 'surface-warm variant' },
    // Light mode — CTA (must clear brand-mandated 7:1 AAA for projected board displays).
    { mode: 'light', fg: 'accentCtaText', bg: 'accentCtaBg', role: 'cta' },
    // Light mode — regulatory-purple citation chip must be readable as body.
    { mode: 'light', fg: 'regulatory', bg: 'bg', role: 'body', note: 'v0.2 correction — 5.2:1' },
    // Cluster 0 additions (PR-C0-1) — chip pairs must clear 4.5:1 for
    // status labels on their muted bg fills.
    { mode: 'dark', fg: 'successFg', bg: 'successBg', role: 'body', note: 'chip pair — 8.1:1 measured' },
    { mode: 'dark', fg: 'warningFg', bg: 'warningBg', role: 'body', note: 'chip pair — 8.8:1 measured' },
    { mode: 'dark', fg: 'dangerFg', bg: 'dangerBg', role: 'body', note: 'chip pair — 6.3:1 measured' },
    { mode: 'light', fg: 'successFg', bg: 'successBg', role: 'body', note: 'chip pair — 4.85:1 measured' },
    { mode: 'light', fg: 'warningFg', bg: 'warningBg', role: 'body', note: 'chip pair — 7.10:1 measured' },
    { mode: 'light', fg: 'dangerFg', bg: 'dangerBg', role: 'body', note: 'chip pair — 5.87:1 measured' },
    // textInverse must clear AAA 7:1 on `accentCtaBg` for CTA legibility
    // on projected board displays (matches the pre-existing accentCtaText/
    // accentCtaBg pair; this second pair validates textInverse is a safe
    // stand-in when a surface prefers the semantic name).
    { mode: 'dark', fg: 'textInverse', bg: 'accentCtaBg', role: 'cta' },
    { mode: 'light', fg: 'textInverse', bg: 'accentCtaBg', role: 'cta' },
    // PR-P3-2 (UC-4 Alerts) — category icon-tile chip pairs.
    { mode: 'dark', fg: 'litigation', bg: 'litigationBg', role: 'body', note: 'chip pair — 10.46:1 measured' },
    { mode: 'light', fg: 'litigation', bg: 'litigationBg', role: 'body', note: 'chip pair — 9.26:1 measured' },
    { mode: 'dark', fg: 'regulatory', bg: 'regulatoryBg', role: 'body', note: 'chip pair — 7.95:1 measured' },
    { mode: 'light', fg: 'regulatory', bg: 'regulatoryBg', role: 'body', note: 'chip pair — 5.57:1 measured' },
    { mode: 'dark', fg: 'secondaryFg', bg: 'secondaryBg', role: 'body', note: 'chip pair — 7.56:1 measured' },
    { mode: 'light', fg: 'secondaryFg', bg: 'secondaryBg', role: 'body', note: 'chip pair — 5.62:1 measured' },
    // V98-0-6 (C23) — form-control edge is load-bearing (WCAG 1.4.11):
    // every surface tier, both modes.
    { mode: 'dark', fg: 'borderControl', bg: 'bg', role: 'ui', note: 'form edge' },
    { mode: 'dark', fg: 'borderControl', bg: 'surface', role: 'ui', note: 'form edge' },
    { mode: 'dark', fg: 'borderControl', bg: 'surfaceElevated', role: 'ui', note: 'form edge' },
    { mode: 'dark', fg: 'borderControl', bg: 'surfaceAlt', role: 'ui', note: 'form edge' },
    { mode: 'light', fg: 'borderControl', bg: 'bg', role: 'ui', note: 'form edge' },
    { mode: 'light', fg: 'borderControl', bg: 'surface', role: 'ui', note: 'form edge' },
    { mode: 'light', fg: 'borderControl', bg: 'surfaceElevated', role: 'ui', note: 'form edge' },
    { mode: 'light', fg: 'borderControl', bg: 'surfaceAlt', role: 'ui', note: 'form edge' },
];
//# sourceMappingURL=colors.js.map